import { Field, Formik } from 'formik';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, InlineLoading, InlineNotification, Select, SelectItem, Stack, TextInput } from '@carbon/react';
import { useStorefront, useStorefrontSlugAvailability, useUpdateStorefrontMutation } from 'src/hooks/useStorefront';
import { StorefrontSlugAvailabilityDto } from 'src/services/apis/types';

/** Long enough that typing a slug does not fire a request per keystroke. */
const SLUG_CHECK_DEBOUNCE_MS = 400;

type StorefrontSiteForm = {
  slug: string;
  defaultLocale: 'vi' | 'en';
};

const StorefrontSite = React.memo(() => {
  const { t } = useTranslation();
  const { data: storefront } = useStorefront();
  const { mutate: updateStorefront, isLoading: updating, error: updateError } = useUpdateStorefrontMutation();
  const checkSlugAvailability = useStorefrontSlugAvailability();

  const [slugFeedback, setSlugFeedback] = useState<StorefrontSlugAvailabilityDto | null>(null);
  const slugCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A published storefront's slug is a one-way door — the server rejects the change, so the field
  // is locked rather than left editable and failing on submit.
  const published = Boolean(storefront?.publishedAt);
  const storedSlug = storefront?.slug ?? '';
  // From the API, not guessed from the portal's own hostname. `STOREFRONT_BASE_DOMAIN` is the
  // only authority for this address and it lives on the server, which composes the whole origin
  // — scheme and port included. Deriving it here from the last two labels of `window.location`
  // produced `https://pho-24.localhost` in development (no port, wrong scheme), a dead domain
  // wherever the portal and the storefronts are not on one domain, and `slug.co.uk` on a ccTLD.
  const publicUrl = storefront?.publicUrl ?? null;

  useEffect(() => {
    return () => {
      if (slugCheckTimer.current) {
        clearTimeout(slugCheckTimer.current);
      }
    };
  }, []);

  const scheduleSlugCheck = useCallback(
    (slug: string) => {
      if (slugCheckTimer.current) {
        clearTimeout(slugCheckTimer.current);
      }
      // An empty field or the stored slug is not worth a round trip — only a real edit is.
      if (!slug || slug === storedSlug) {
        setSlugFeedback(null);
        return;
      }
      slugCheckTimer.current = setTimeout(() => {
        checkSlugAvailability(slug)
          .then((result) => setSlugFeedback(result))
          .catch(() => setSlugFeedback(null));
      }, SLUG_CHECK_DEBOUNCE_MS);
    },
    [checkSlugAvailability, storedSlug]
  );

  const handleSubmit = useCallback(
    (values: StorefrontSiteForm) => {
      updateStorefront({ slug: values.slug, defaultLocale: values.defaultLocale });
    },
    [updateStorefront]
  );

  function slugHelperText(): string {
    if (published) {
      return t('storefront.site.slugLocked');
    }
    if (slugFeedback) {
      return slugFeedback.available ? t('storefront.slug.available') : t(`storefront.slug.${slugFeedback.reason}`);
    }
    return t('storefront.site.slugHint');
  }

  const slugInvalid = Boolean(slugFeedback && !slugFeedback.available);

  return (
    <Formik<StorefrontSiteForm>
      initialValues={{ slug: storedSlug, defaultLocale: storefront?.defaultLocale ?? 'vi' }}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ handleSubmit: submitForm, values, setFieldValue }) => (
        <Form onSubmit={submitForm}>
          <Stack gap={6}>
            {/* Whether customers can see the site, as a Carbon notification: informational while it
                is a draft, success once published. Publish itself is in the area header. */}
            <InlineNotification
              className="storefront-site__status"
              kind={published ? 'success' : 'info'}
              lowContrast
              hideCloseButton
              title={published ? t('storefront.site.publishedAt') : t('storefront.site.draft')}
            />

            <Field name="slug">
              {({ field }) => (
                <TextInput
                  id="storefront-slug"
                  labelText={t('storefront.site.slug')}
                  helperText={slugHelperText()}
                  disabled={published}
                  invalid={slugInvalid}
                  invalidText={slugInvalid ? slugHelperText() : undefined}
                  {...field}
                  onChange={(event) => {
                    field.onChange(event);
                    scheduleSlugCheck(event.target.value);
                  }}
                />
              )}
            </Field>

            <Select
              id="storefront-default-locale"
              labelText={t('storefront.site.defaultLocale')}
              value={values.defaultLocale}
              onChange={(event) => setFieldValue('defaultLocale', event.target.value)}
            >
              <SelectItem value="vi" text="Tiếng Việt" />
              <SelectItem value="en" text="English" />
            </Select>

            {published && publicUrl && (
              <p className="storefront-site__address">
                {t('storefront.site.address')}:{' '}
                <a href={publicUrl} target="_blank" rel="noreferrer">
                  {publicUrl}
                </a>
              </p>
            )}

            <div className="storefront-site__actions">
              {updating ? (
                <InlineLoading description={`${t('actions.save')}...`} />
              ) : (
                <Button type="submit">{t('storefront.actions.save')}</Button>
              )}
            </div>

            {updateError && <InlineNotification kind="error" subtitle={t('messages.somethingWentWrong')} />}
          </Stack>
        </Form>
      )}
    </Formik>
  );
});

export default StorefrontSite;
